<template>
  <VTooltip
    v-bind="$attrs"
    open-on-click
    :location="bottomAligned ? 'bottom' : 'top'"
    :open-on-hover="false"
    :content-class="tooltipStyle"
    :offset="computedOffset"
    :max-width="DIALOG_MAX_WIDTH"
    open-delay="500"
    :eager="false"
    class="pa-3"
  >
    <template #activator="{ props: propsTooltip }">
      <SBtn
        v-show="!text"
        ref="tooltipBtn"
        v-scroll="onScroll"
        v-resize="onResize"
        :height="size"
        :width="size"
        variant="text"
        size="small"
        :aria-label="ariaHelp ?? $t('tooltip.aria-label')"
        v-bind="propsTooltip"
      >
        <!-- The name goes on the button: Vuetify hides the icon from assistive
             tech, so a label there left the button unnamed. AB#65419 -->
        <SIcon :icon="icons.mdiHelpCircleOutline" :color="color" :size="size" decorative />
      </SBtn>
      <SBtn
        v-show="text"
        ref="tooltipLink"
        link-medium
        class="link-btn"
        v-bind="propsTooltip"
      >
        {{ text }}
      </SBtn>
    </template>
    <slot />
  </VTooltip>
</template>
<script lang="ts" setup>
import { mdiHelpCircleOutline } from '@mdi/js'
import SBtn from '../components/SBtn.vue'
import SIcon from '../components/SIcon.vue'
import {useTemplateRef} from "vue";

// $attrs is bound to VTooltip explicitly; inheriting them as well put the class
// and other attributes on twice (Vue merges a listener bound both ways into one,
// so listeners were never doubled). eager is off (Vuetify 4 defaults it on) so an
// empty role="tooltip" element is not in the page before it first opens. AB#65419
defineOptions({
  inheritAttrs: false
})

interface Props {
  text?: string
  size?: string
  color?: string
  callout?: boolean
  position?: string
  ariaHelp?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: '1.5rem',
  color: 'hover',
  text: undefined,
  position: '',
  // Unset, the button is named by the translated tooltip.aria-label. Stated
  // only for vue/require-default-prop. AB#65419
  ariaHelp: undefined
})

enum CalloutPosition {
  LEFT,
  MIDDLE,
  RIGHT
}

const DIALOG_MAX_WIDTH = 320
const DIALOG_HALF_WIDTH = 160
const NUDGE_LEFT_OR_RIGHT = 134

const icons = { mdiHelpCircleOutline }

const offsetTopButton = ref(0)
const offsetLeftButton = ref(0)
const halfWindowHeight = ref(0)
const leftEdgeConstraint = ref(0)
const rightEdgeConstraint = ref(0)
const bottomAligned = ref(false)

const tooltipBtn = useTemplateRef('tooltipBtn')
const tooltipLink = useTemplateRef('tooltipLink')

const tooltipStyle = computed(() => {
  let classList = ''
  const position: CalloutPosition = CalloutPosition[props.position.toUpperCase() as keyof typeof CalloutPosition]
  classList = props.callout ? 'callout' : 'tooltip'
  classList = (offsetTopButton.value <= halfWindowHeight.value) ? `${classList} bottom` : `${classList} top`
  if (position === CalloutPosition.MIDDLE) {
    classList = `${classList} middle`
  } else if (position === CalloutPosition.LEFT || offsetLeftButton.value <= leftEdgeConstraint.value) {
    classList = `${classList} left`
  } else if (position === CalloutPosition.RIGHT || offsetLeftButton.value >= rightEdgeConstraint.value) {
    classList = `${classList} right`
  } else {
    classList = `${classList} middle`
  }
  return classList
})

const computedOffset = computed(() => {
  if (offsetLeftButton.value <= leftEdgeConstraint.value) return [NUDGE_LEFT_OR_RIGHT, 0]
  if (offsetLeftButton.value >= rightEdgeConstraint.value) return [-NUDGE_LEFT_OR_RIGHT, 0]
  return [0, 0]
})
const setViewableWindowSize = () => {
  halfWindowHeight.value = window.innerHeight / 2
  leftEdgeConstraint.value = DIALOG_HALF_WIDTH
  rightEdgeConstraint.value = window.innerWidth - DIALOG_HALF_WIDTH
}
const setTooltipOffset = async () => {
  const button = tooltipBtn.value?.$el as HTMLElement
  const link = tooltipLink.value?.$el as HTMLElement
  const element = props.text ? link : button
  await nextTick()
  const { top, left } = getOffset(element)
  offsetTopButton.value = top - window.scrollY
  offsetLeftButton.value = left
  bottomAligned.value = offsetTopButton.value <= halfWindowHeight.value
}
const getOffset = (element: HTMLElement) => {
  let x = 0
  let y = 0

  while (element && !isNaN(element.offsetLeft) && !isNaN(element.offsetTop)) {
    x += element.offsetLeft - element.scrollLeft
    y += element.offsetTop - element.scrollTop
    element = element.offsetParent as HTMLElement
  }
  return { top: y, left: x }
}

const onResize = async () => {
  setViewableWindowSize()
  await setTooltipOffset()
}

const onScroll = async () => {
  await setTooltipOffset()
}

onMounted(() => {
  if (props.position && !Object.values(CalloutPosition).includes(props.position.toUpperCase())) {
    throw new Error('Tooltip position must be one of the following: undefined | left | middle | right')
  }
  setViewableWindowSize()
  setTooltipOffset()
})

defineExpose({
  offsetLeftButton,
  offsetTopButton,
  getOffset
})

</script>
<style lang="scss">
// Non-scoped: VTooltip content-class renders in a VOverlay portal.
@layer app-trumps {
  .tooltip,
  .callout {
    padding: 24px;
    opacity: 1;
    box-shadow: rgba(0, 0, 0, 0.3) 4px 4px 4px;
    border: 1px solid rgb(var(--v-theme-border));

    &.top {
      margin-top: -8px;
    }

    &.bottom {
      margin-top: 8px;
    }
  }

  .callout:after {
    height: 20px;
    width: 20px;
    content: "";
    background-color: white;
    position: absolute;
    transform: rotate(-45deg);
    -moz-transform: rotate(-45deg);
    -webkit-transform: rotate(-45deg);
  }

  .left:after {
    left: 16px;
  }

  .middle:after {
    left: 47%;
  }

  .right:after {
    right: 16px;
  }

  .top:after {
    bottom: -10px;
    border-left: 1px solid rgb(var(--v-theme-border));
    border-bottom: 1px solid rgb(var(--v-theme-border));
  }

  .bottom:after {
    top: -10px;
    border-right: 1px solid rgb(var(--v-theme-border));
    border-top: 1px solid rgb(var(--v-theme-border));
  }
}
</style>
